import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CATEGORIES, TIERS, VENUE_TYPES, CUISINES, PRESETS, newUnit, scoreUnit, detectTourism, detectSeasonality, genNotes } from '../utils/scoring';
import { getOperator, saveOperator } from '../utils/storage';

const LMH = ['Low', 'Medium', 'High'];
const ASSESSMENTS = [
  ['luxury', 'Luxury Positioning'], ['activation', 'Activation Appetite'], ['bevComplexity', 'Beverage Menu Complexity'],
  ['reporting', 'Reporting Readiness'], ['suppliers', 'Existing Supplier Relationships'], ['training', 'Training Need'],
  ['events', 'Event / Private Dining Volume'], ['social', 'Social / Press Visibility'], ['fbLeadership', 'Above-Property F&B Leadership'],
];

export default function Calculator() {
  const { operatorId } = useParams();
  const navigate = useNavigate();
  const [operatorName, setOperatorName] = useState('');
  const [units, setUnits] = useState([newUnit('Unit 1')]);
  const [activeUnit, setActiveUnit] = useState(0);
  const [view, setView] = useState('form');
  const [saveId, setSaveId] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);
  const saveRef = useRef(null);

  useEffect(() => {
    if (operatorId) {
      getOperator(operatorId).then(op => {
        if (op?.data) {
          setOperatorName(op.data.operatorName || op.operator_name || '');
          setUnits(op.data.units || [newUnit()]);
          setSaveId(op.id);
        }
      });
    }
  }, [operatorId]);

  const u = units[activeUnit] || units[0];
  const fmt = n => '$' + Math.round(n || 0).toLocaleString();

  const updateUnit = (field, value) => {
    const updated = [...units];
    updated[activeUnit] = { ...updated[activeUnit], [field]: value };
    setUnits(updated);
  };

  const addUnit = () => { setUnits([...units, newUnit(`Unit ${units.length + 1}`)]); setActiveUnit(units.length); };
  const removeUnit = (i) => { if (units.length <= 1) return; setUnits(units.filter((_, j) => j !== i)); setActiveUnit(Math.min(activeUnit, units.length - 2)); };
  const renameUnit = () => { const n = prompt('Unit name:', u.name); if (n) updateUnit('name', n); };

  const calculateAll = () => {
    const updated = units.map(unit => {
      const total = +unit.totalSales;
      if (!total) return unit;
      return { ...unit, results: scoreUnit(unit) };
    });
    setUnits(updated);
    setActiveUnit(0);
    setView('results');
  };

  const doSave = async () => {
    setSaveStatus('saving');
    try {
      const result = await saveOperator({ id: saveId, operatorName, units });
      if (result?.id) setSaveId(result.id);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 2000);
    } catch { setSaveStatus(null); }
  };

  const exportCSV = () => {
    const rows = [['Operator', 'Unit', 'Market', 'Cuisine', 'Covers/wk', 'Category', 'Sales', 'Ratio', 'Rev', 'Ratio', 'Act', 'Final', 'Tier']];
    units.filter(u => u.results).forEach(u => {
      u.results.forEach(r => {
        rows.push([operatorName, u.name, u.market, u.cuisine, u.coversPerWeek || '', r.name, r.sales, (r.ratio * 100).toFixed(1) + '%', r.revScore, r.ratScore, r.actScore, r.finalScore, `T${r.tier.num} ${r.tier.name}`]);
      });
    });
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv' }));
    a.download = `${operatorName || 'operator'}_tier.csv`; a.click();
  };

  if (view === 'results') {
    const scored = units.filter(u => u.results);
    const au = scored[activeUnit] || scored[0];
    const tourism = au ? detectTourism(au.market) : '';
    const season = au ? detectSeasonality(au.market) : '';

    return (
      <div className="py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-xl text-brand-gold">{operatorName || 'Operator'} — Portfolio Summary</h1>
            <p className="text-xs text-neutral-500">{scored.length} unit(s)</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => navigate('/')} className="btn-ghost text-xs">← Dashboard</button>
            <button onClick={() => setView('form')} className="btn-secondary text-xs py-2">Edit</button>
            <button onClick={doSave} className="btn-secondary text-xs py-2">{saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved ✓' : 'Save'}</button>
            <button onClick={exportCSV} className="btn-secondary text-xs py-2">CSV</button>
            <button onClick={() => window.print()} className="btn-primary text-xs py-2">PDF</button>
          </div>
        </div>

        {/* Portfolio Table */}
        <div className="card p-5 mb-6">
          <h2 className="text-sm font-semibold text-brand-gold mb-4">Portfolio Overview</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="border-b border-neutral-800">
                <th className="text-left py-2 pr-3 text-neutral-500">Unit</th><th className="text-left py-2 text-neutral-500">Market</th><th className="text-left py-2 text-neutral-500">Cuisine</th><th className="text-right py-2 text-neutral-500">Covers/wk</th><th className="text-right py-2 text-neutral-500">Total Rev</th>
                {CATEGORIES.map(c => <th key={c.id} className="text-center py-2 text-neutral-500">{c.icon}</th>)}
              </tr></thead>
              <tbody>{scored.map((su, i) => (
                <tr key={i} className="border-b border-neutral-800/30 cursor-pointer hover:bg-neutral-800/20" onClick={() => setActiveUnit(i)}>
                  <td className="py-2 pr-3 font-medium text-white">{su.name}</td>
                  <td className="py-2 text-neutral-400 text-[11px]">{su.market}</td>
                  <td className="py-2 text-neutral-500 text-[11px]">{su.cuisine}</td>
                  <td className="py-2 text-right text-neutral-500">{su.coversPerWeek || '—'}</td>
                  <td className="py-2 text-right">{fmt(+su.totalSales)}</td>
                  {su.results.map(r => <td key={r.id} className="text-center py-2"><span className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-semibold ${r.tier.cls}`}>T{r.tier.num}</span><br /><span className="text-[9px] text-neutral-600">{r.finalScore}</span></td>)}
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>

        {/* Unit tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {scored.map((su, i) => (
            <button key={i} onClick={() => setActiveUnit(i)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${i === activeUnit ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/40' : 'bg-neutral-800/40 text-neutral-500 border border-neutral-800'}`}>{su.name}</button>
          ))}
        </div>

        {/* Unit detail */}
        {au && au.results && (
          <>
            <div className="card p-5 mb-4">
              <h2 className="text-sm font-semibold text-white mb-1">{au.name} — {au.market}</h2>
              <p className="text-[10px] text-neutral-500 mb-4">{au.cuisine} · {au.coversPerWeek ? au.coversPerWeek + ' covers/wk · ' : ''}Tourism: {tourism} · Seasonality: {season} · Spend: {au.avgGuestSpend ? '$' + au.avgGuestSpend : '—'} / {au.avgAlcSpend ? '$' + au.avgAlcSpend : '—'}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {au.results.map(r => (
                  <div key={r.id} className="bg-neutral-800/30 border border-neutral-800 rounded-xl p-4 text-center" style={{ borderTopColor: r.tier.color, borderTopWidth: 3 }}>
                    <p className="text-[10px] text-neutral-500 uppercase tracking-wider">{r.icon} {r.name}</p>
                    <p className="text-3xl font-black mt-1" style={{ color: r.tier.color }}>T{r.tier.num}</p>
                    <p className="text-xs font-semibold mt-1" style={{ color: r.tier.color }}>{r.tier.name}</p>
                    <p className="text-[10px] text-neutral-600 mt-1">Score: {r.finalScore}/100</p>
                    <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden mt-2">
                      <div className="h-full rounded-full" style={{ width: `${r.finalScore}%`, backgroundColor: r.tier.color }} />
                    </div>
                    <p className="text-[8px] text-neutral-600 mt-2 leading-relaxed">{r.tier.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Detail table */}
            <div className="card p-5 mb-4">
              <h2 className="text-sm font-semibold text-brand-gold mb-3">Category Detail</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-neutral-800"><th className="text-left py-2">Category</th><th className="text-right py-2">Sales</th><th className="text-right py-2">Ratio</th><th className="text-center py-2">Rev</th><th className="text-center py-2">Ratio</th><th className="text-center py-2">Activation</th><th className="text-center py-2">Final</th><th className="text-center py-2">Support Level</th></tr></thead>
                  <tbody>{au.results.map(r => (
                    <tr key={r.id} className="border-b border-neutral-800/30">
                      <td className="py-2 font-medium">{r.icon} {r.name}</td>
                      <td className="py-2 text-right">{fmt(r.sales)}</td>
                      <td className="py-2 text-right">{(r.ratio * 100).toFixed(1)}%</td>
                      <td className="py-2 text-center">{r.revScore}</td>
                      <td className="py-2 text-center">{r.ratScore}</td>
                      <td className="py-2 text-center">{r.actScore}</td>
                      <td className="py-2 text-center font-bold">{r.finalScore}</td>
                      <td className="py-2 text-center"><span className={`tier-pill ${r.tier.cls}`}>Tier {r.tier.num} — {r.tier.name}</span></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>

            {/* Category breakdowns */}
            {au.results.map(r => (
              <div key={r.id} className="card p-5 mb-3">
                <h3 className="text-sm font-semibold mb-3" style={{ color: r.tier.color }}>{r.icon} {r.name} — Tier {r.tier.num} ({r.tier.name})</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    {[['Annual Sales', fmt(r.sales)], ['Category Ratio', (r.ratio * 100).toFixed(1) + '%'], ['Revenue Score (45%)', r.revScore]].map(([l, v]) => (
                      <div key={l} className="flex justify-between py-1 border-b border-neutral-800/50"><span className="text-[11px] text-neutral-500">{l}</span><span className="text-xs font-semibold">{v}</span></div>
                    ))}
                  </div>
                  <div className="space-y-1">
                    {[['Ratio Score (20%)', r.ratScore], ['Activation Value (35%)', r.actScore], ['Final Score', r.finalScore]].map(([l, v]) => (
                      <div key={l} className="flex justify-between py-1 border-b border-neutral-800/50"><span className="text-[11px] text-neutral-500">{l}</span><span className="text-xs font-semibold" style={l === 'Final Score' ? { color: r.tier.color, fontSize: 16 } : {}}>{v}{l === 'Final Score' ? '/100' : ''}</span></div>
                    ))}
                  </div>
                </div>
                <div className="mt-3 p-3 bg-neutral-950 rounded-lg">
                  <p className="text-[9px] text-neutral-600 uppercase tracking-wider mb-1">Assessment Notes</p>
                  <p className="text-[11px] text-neutral-400 leading-relaxed">{genNotes(r, au)}</p>
                </div>
              </div>
            ))}
          </>
        )}

        <p className="text-center text-[8px] text-neutral-700 mt-6">The NEG Proprietary Category Support Scoring System is intended to evaluate category-specific support intensity, activation opportunity, reporting requirements, and strategic hospitality value. Commercial terms, supplier participation, activation budgets, and final support scope are determined separately by NEG.</p>
      </div>
    );
  }

  // Form view
  const tourism = detectTourism(u.market);
  const season = detectSeasonality(u.market);

  return (
    <div className="py-6">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate('/')} className="btn-ghost text-xs">← Dashboard</button>
        <button onClick={doSave} className="btn-secondary text-xs py-2">{saveStatus === 'saving' ? 'Saving...' : saveStatus === 'saved' ? 'Saved ✓' : 'Save'}</button>
      </div>

      <div className="card p-5 mb-4">
        <h2 className="text-sm font-semibold text-brand-gold mb-3">Operator</h2>
        <div className="form-grid">
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Operator / Group Name</label><input className="input-field" value={operatorName} onChange={e => setOperatorName(e.target.value)} /></div>
        </div>
      </div>

      {/* Unit tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {units.map((unit, i) => (
          <button key={i} onClick={() => setActiveUnit(i)} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${i === activeUnit ? 'bg-brand-gold/15 text-brand-gold border border-brand-gold/40' : 'bg-neutral-800/40 text-neutral-500 border border-neutral-800'}`}>{unit.name}</button>
        ))}
        <button onClick={addUnit} className="px-3 py-2 rounded-lg text-xs text-neutral-600 border border-dashed border-neutral-700 hover:text-neutral-400">+ Add Unit</button>
      </div>

      {/* Unit form */}
      <div className="card p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-white">{u.name}</h2>
          <div className="flex gap-2">
            <button onClick={renameUnit} className="btn-secondary text-[10px] py-1 px-3">Rename</button>
            {units.length > 1 && <button onClick={() => removeUnit(activeUnit)} className="text-[10px] text-neutral-600 hover:text-red-400 px-2">Remove</button>}
          </div>
        </div>
        <div className="mb-4">
          <p className="text-[10px] text-neutral-600 mb-2">Quick-fill from template (keeps name, market, address):</p>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map(p => (
              <button key={p.id} onClick={() => {
                const updated = [...units];
                const cur = updated[activeUnit];
                const { id: _id, label: _label, ...preset } = p;
                updated[activeUnit] = { ...cur, ...preset, name: cur.name, market: cur.market, address: cur.address, results: null };
                setUnits(updated);
              }} className="text-[10px] px-2.5 py-1.5 rounded-lg bg-neutral-800/50 text-neutral-500 border border-neutral-800 hover:text-brand-gold hover:border-brand-gold/30 transition-all">
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Market / City</label><input className="input-field text-sm py-2" value={u.market} onChange={e => updateUnit('market', e.target.value)} placeholder="Miami, FL" /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Address</label><input className="input-field text-sm py-2" value={u.address} onChange={e => updateUnit('address', e.target.value)} /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Venue Type</label><select className="input-field text-sm py-2" value={u.type} onChange={e => updateUnit('type', e.target.value)}>{VENUE_TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Cuisine Type</label><select className="input-field text-sm py-2" value={u.cuisine} onChange={e => updateUnit('cuisine', e.target.value)}>{CUISINES.map(t => <option key={t}>{t}</option>)}</select></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Outlets / Bars</label><input type="number" className="input-field text-sm py-2" value={u.outlets} min={1} onChange={e => updateUnit('outlets', +e.target.value)} /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">POS / Craftable</label><select className="input-field text-sm py-2" value={u.pos} onChange={e => updateUnit('pos', e.target.value)}>{['Yes', 'No', 'Planned'].map(o => <option key={o}>{o}</option>)}</select></div>
        </div>
        <div className="flex gap-4 mt-3 text-[10px] text-neutral-600">
          <span>Tourism: <span style={{ color: tourism === 'High' ? '#C9A668' : tourism === 'Medium' ? '#60a5fa' : '#999' }}>{tourism}</span></span>
          <span>Seasonality: <span style={{ color: season === 'High' ? '#fb923c' : season === 'Medium' ? '#fbbf24' : '#999' }}>{season}</span></span>
          <span className="text-neutral-700">(auto-detected)</span>
        </div>
      </div>

      <div className="card p-5 mb-4">
        <h2 className="text-sm font-semibold text-brand-gold mb-3">Revenue & Spend</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Total Annual Bev Sales ($)</label><input type="number" className="input-field text-sm py-2" value={u.totalSales} onChange={e => updateUnit('totalSales', e.target.value)} placeholder="6,870,000" /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Avg Guest Spend ($)</label><input type="number" className="input-field text-sm py-2" value={u.avgGuestSpend} onChange={e => updateUnit('avgGuestSpend', e.target.value)} placeholder="85" /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Avg Alc Spend / Guest ($)</label><input type="number" className="input-field text-sm py-2" value={u.avgAlcSpend} onChange={e => updateUnit('avgAlcSpend', e.target.value)} placeholder="32" /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Covers per Week</label><input type="number" className="input-field text-sm py-2" value={u.coversPerWeek} onChange={e => updateUnit('coversPerWeek', e.target.value)} placeholder="1200" /></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Spirits ($)</label><input type="number" className="input-field text-sm py-2" value={u.spiritsSales} onChange={e => updateUnit('spiritsSales', e.target.value)} /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Wine ($)</label><input type="number" className="input-field text-sm py-2" value={u.wineSales} onChange={e => updateUnit('wineSales', e.target.value)} /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Beer ($)</label><input type="number" className="input-field text-sm py-2" value={u.beerSales} onChange={e => updateUnit('beerSales', e.target.value)} /></div>
          <div className="form-group"><label className="text-[11px] text-neutral-500 mb-1">Non-Alcoholic ($)</label><input type="number" className="input-field text-sm py-2" value={u.naSales} onChange={e => updateUnit('naSales', e.target.value)} /></div>
        </div>
      </div>

      <div className="card p-5 mb-4">
        <h2 className="text-sm font-semibold text-brand-gold mb-3">Unit Assessment</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {ASSESSMENTS.map(([key, label]) => (
            <div key={key} className="form-group">
              <label className="text-[11px] text-neutral-500 mb-1">{label}</label>
              <select className="input-field text-sm py-2" value={u[key]} onChange={e => updateUnit(key, e.target.value)}>
                {LMH.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-center gap-3 mt-6">
        <button onClick={calculateAll} className="btn-primary text-sm py-3 px-8">Calculate All Unit Tiers</button>
      </div>
    </div>
  );
}
